import { searchClient } from '@algolia/client-search'
import alpine from 'alpinejs'

export default function () {
	return {
		config: window.searchConfig || {},
		query: '',
		status: null,
		selectedFacets: {},
		minPrice: null,
		maxPrice: null,

		init() {
			this.registerStore()
			this.attachEvents()
			this.$watch('query', () => this.search())
		},

		registerStore() {
			alpine.store('search', {
				results: {},
				total: 0,
				facets: {},
				selectedFacets: this.selectedFacets,
				showLevel1Categories: false,
				showLevel2Categories: false,
				query: this.query,
				performed: false,

				attributeFacets: () => ({}),
				facetName: () => '',
			})
		},

		attachEvents() {
			window.addEventListener('search:set-query', (event) => {
				this.query = event.detail.query
			})

			window.addEventListener('search:set-filter', (event) => {
				this.setFacet(event)
			})

			window.addEventListener('search:set-price-range', (event) => {
				this.minPrice = Number.isNaN(event.detail.min) ? null : event.detail.min
				this.maxPrice = Number.isNaN(event.detail.max) ? null : event.detail.max

				this.search()
			})
		},

		search() {
			if (this.query.length < 2) {
				return
			}

			const storeQuery = alpine.store('search').query
			if (storeQuery !== this.query) {
				this.selectedFacets = {}
				this.minPrice = null
				this.maxPrice = null
			}

			this.status = 'loading'

			const client = searchClient(this.config.appId, this.config.key)

			client
				.searchSingleIndex({
					indexName: this.config.index,
					searchParams: {
						query: this.query,
						facets: ['*'],
						hitsPerPage: 100,
						facetFilters: this.getFacetFilters(),
						numericFilters: this.getNumericFilters(),
					},
				})
				.then((response) => {
					this.pushState()
					this.modifyMetaTitle()

					const transformedFacets = Object.fromEntries(
						Object.entries(response.facets).map(([attribute, values]) => [
							attribute,
							Object.entries(values).map(([name, count]) => ({ name, count })),
						]),
					)

					alpine.store('search', {
						results: response.hits,
						total: response.nbHits,
						facets: transformedFacets,
						selectedFacets: this.selectedFacets,
						showLevel1Categories: response.facets['categories.lvl1'] && Object.keys(this.selectedFacets['categories.lvl0'] ?? {}).length > 0,
						showLevel2Categories: response.facets['categories.lvl2'] && Object.keys(this.selectedFacets['categories.lvl1'] ?? {}).length > 0,
						query: this.query,
						performed: true,

						attributeFacets() {
							return Object.fromEntries(
								Object.entries(transformedFacets).filter(facet => facet[0].startsWith('attribute_')),
							)
						},

						facetName(facet) {
							const facetName = facet.replace('attribute_', '').replace('_', '-')

							if (facetName in window.searchConfig.i18n.facetNames) {
								return window.searchConfig.i18n.facetNames[facetName]
							}

							return facetName
						},
					})

					document.querySelector('main')?.classList.add('!hidden')
					document.querySelector('.breadcrumbs')?.classList.add('!hidden')
				})
				.catch(error => console.error(error))
				.finally(() => this.status = null)
		},

		pushState() {
			window.history.pushState(
				{ },
				null,
				`/?s=${this.query}`,
			)
		},

		modifyMetaTitle() {
			const title = this.config.i18n.metaTitle.replace(
				'%s',
				this.query,
			)
			document.title = title
		},

		setFacet(event) {
			const facetName = event.detail.facet
			const facetValue = event.detail.value

			if (!this.selectedFacets[facetName]) {
				this.selectedFacets[facetName] = [facetValue]
				this.search()

				return
			}

			if (this.selectedFacets[facetName].includes(facetValue)) {
				this.selectedFacets[facetName] = this.selectedFacets[facetName].filter(value => value !== facetValue)

				if (facetName === 'categories.lvl0') {
					this.selectedFacets['categories.lvl1'] = []
					this.selectedFacets['categories.lvl2'] = []
				}

				if (facetName === 'categories.lvl1') {
					this.selectedFacets['categories.lvl2'] = []
				}
			}
			else {
				this.selectedFacets[facetName].push(facetValue)
			}

			this.search()
		},

		getNumericFilters() {
			if (this.minPrice && this.maxPrice) {
				return [`price >= ${this.minPrice}`, `price <= ${this.maxPrice}`]
			}

			if (this.minPrice) {
				return [`price >= ${this.minPrice}`]
			}

			if (this.maxPrice) {
				return [`price <= ${this.maxPrice}`]
			}

			return []
		},

		getFacetFilters() {
			return Object.entries(this.selectedFacets)
				.filter(([, values]) => values.length)
				.map(([facet, values]) => {
					return values.map(value => `${facet}:${value}`)
				})
				.reduce((acc, val) => acc.concat(val), [])
		},
	}
}
